import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';

import HistoryPanel from '~/components/HistoryPanel';
import ShareModal, { ShareStats } from '~/components/ShareModal';
import { saveHistoryEntry } from '~/utils/history';
import { Difficulty, generateWords, WordData } from '~/utils/words';

/* ─── TYPES ─────────────────────────────────────────────────────────────── */
type TimerMode = 15 | 30 | 60 | 120;
type CharStatus = 'pending' | 'correct' | 'incorrect';

interface Stats {
  wpm: number;
  rawWpm: number;
  accuracy: number;
  correct: number;
  incorrect: number;
  extra: number;
  missed: number;
}

interface WpmPoint {
  second: number;
  wpm: number;
  rawWpm: number;
}

interface Rating {
  label: string;
  description: string;
  color: string;
}

/* ─── HELPERS ───────────────────────────────────────────────────────────── */
function computeStats(words: WordData[], elapsed: number): Stats {
  if (elapsed <= 0) {
    return { wpm: 0, rawWpm: 0, accuracy: 100, correct: 0, incorrect: 0, extra: 0, missed: 0 };
  }
  let correctChars = 0, incorrectChars = 0, extraChars = 0, missedChars = 0, totalTyped = 0;
  for (const w of words) {
    if (!w.typed && !w.completed) continue;
    totalTyped += w.typed.length;
    const maxLen = Math.max(w.target.length, w.typed.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < w.target.length && i < w.typed.length) {
        w.typed[i] === w.target[i] ? correctChars++ : incorrectChars++;
      } else if (i >= w.target.length) {
        extraChars++;
      } else if (w.completed) {
        missedChars++;
      }
    }
  }
  const minutes = elapsed / 60;
  const wpm = Math.max(0, Math.round(correctChars / 5 / minutes));
  const rawWpm = Math.max(0, Math.round(totalTyped / 5 / minutes));
  const denom = correctChars + incorrectChars + extraChars;
  const accuracy = denom > 0 ? Math.round((correctChars / denom) * 100) : 100;
  return { wpm, rawWpm, accuracy, correct: correctChars, incorrect: incorrectChars, extra: extraChars, missed: missedChars };
}

function getRating(wpm: number): Rating {
  if (wpm >= 120) return { label: 'Elite', description: "You're in the top 1% of typists worldwide", color: 'var(--color-accent)' };
  if (wpm >= 100) return { label: 'Excellent', description: 'Top 10% of typists — outstanding speed', color: 'var(--color-wpm)' };
  if (wpm >= 80)  return { label: 'Fast', description: 'Faster than 75% of people — impressive', color: 'var(--color-correct)' };
  if (wpm >= 60)  return { label: 'Proficient', description: 'Above average — better than most typists', color: 'var(--color-accuracy)' };
  if (wpm >= 40)  return { label: 'Average', description: 'Around the average typing speed globally', color: 'var(--color-text-secondary)' };
  if (wpm >= 20)  return { label: 'Beginner', description: "Keep practicing — you're making progress!", color: 'var(--color-time)' };
  return { label: 'Starter', description: "Everyone starts here — practice makes perfect", color: 'var(--color-text-muted)' };
}

/* ─── ANIMATED COUNTER ──────────────────────────────────────────────────── */
function useCountUp(target: number, duration = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    setValue(0);
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return value;
}

/* ─── CONSTANTS ─────────────────────────────────────────────────────────── */
const TIMER_MODES: TimerMode[] = [15, 30, 60, 120];
const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard'];
const WORD_POOL = 250;
const MAX_EXTRA = 8;
const LINE_ROWS = 3;

/* ─── STYLED COMPONENTS ─────────────────────────────────────────────────── */
const Wrapper = styled.div`
  margin: 0 auto;
  max-width: var(--max-width-app);
  padding: 0 var(--space-lg);
  width: 100%;
`;

const ModeBar = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  justify-content: center;
  margin-bottom: var(--space-lg);
`;

const Divider = styled.span`
  color: var(--color-border-strong);
  font-size: 0.75rem;
  margin: 0 var(--space-xs);
  user-select: none;
`;

const ModeBtn = styled.button<{ $active?: boolean }>`
  background: ${p => (p.$active ? 'var(--color-accent-glow)' : 'transparent')};
  border: 1px solid ${p => (p.$active ? 'var(--color-accent)' : 'var(--color-border-strong)')};
  border-radius: var(--radius-md);
  color: ${p => (p.$active ? 'var(--color-accent)' : 'var(--color-text-secondary)')};
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-label);
  padding: 5px 14px;
  text-transform: uppercase;
  transition: all var(--transition-base);

  &:hover {
    background: var(--color-bg-subtle);
    border-color: var(--color-accent);
    color: var(--color-text-primary);
  }
`;

const DiffBtn = styled(ModeBtn)<{ $diff?: Difficulty; $active?: boolean }>`
  border-color: ${p =>
    p.$active
      ? p.$diff === 'easy'
        ? 'var(--color-correct)'
        : p.$diff === 'hard'
          ? 'var(--color-incorrect)'
          : 'var(--color-accent)'
      : 'var(--color-border-strong)'};
  color: ${p =>
    p.$active
      ? p.$diff === 'easy'
        ? 'var(--color-correct)'
        : p.$diff === 'hard'
          ? 'var(--color-incorrect)'
          : 'var(--color-accent)'
      : 'var(--color-text-secondary)'};
  background: ${p =>
    p.$active
      ? p.$diff === 'easy'
        ? 'rgba(52,211,153,0.1)'
        : p.$diff === 'hard'
          ? 'rgba(248,113,113,0.1)'
          : 'var(--color-accent-glow)'
      : 'transparent'};
`;

const HistoryBtn = styled(ModeBtn)``;

const LiveBar = styled.div`
  align-items: flex-end;
  display: flex;
  gap: var(--space-xl);
  margin-bottom: var(--space-md);
  min-height: 72px;
  padding: 0 4px;
`;

const StatGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatValue = styled.span<{ $color: string; $sm?: boolean }>`
  color: ${p => p.$color};
  font-family: var(--font-display);
  font-size: ${p => (p.$sm ? '1.5rem' : 'var(--font-size-stat)')};
  font-weight: var(--font-weight-bold);
  line-height: 1;
`;

const StatLabel = styled.span`
  color: var(--color-text-secondary);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-label);
  text-transform: uppercase;
`;

const TypingBox = styled.div<{ $focused: boolean }>`
  background-color: var(--color-bg-surface);
  background-image: repeating-linear-gradient(
    transparent,
    transparent 2px,
    rgba(0, 0, 0, 0.04) 2px,
    rgba(0, 0, 0, 0.04) 4px
  );
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: ${p => (p.$focused ? 'var(--shadow-glow-accent)' : 'var(--shadow-surface)')};
  cursor: text;
  outline: none;
  padding: 40px 48px;
  position: relative;
  transition: box-shadow var(--transition-base);
  user-select: none;
`;

const FocusHint = styled.div<{ $visible: boolean }>`
  align-items: center;
  backdrop-filter: blur(4px);
  background: rgba(10, 13, 18, 0.75);
  border-radius: var(--radius-lg);
  bottom: 0;
  color: var(--color-text-muted);
  display: flex;
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  justify-content: center;
  left: 0;
  letter-spacing: var(--letter-spacing-label);
  opacity: ${p => (p.$visible ? 1 : 0)};
  pointer-events: none;
  position: absolute;
  right: 0;
  text-transform: uppercase;
  top: 0;
  transition: opacity var(--transition-base);
`;

const WordsScroll = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-typing);
  letter-spacing: var(--letter-spacing-typing);
  line-height: var(--line-height-typing);
  overflow: hidden;
  position: relative;
`;

const WordSpan = styled.span<{ $hasError?: boolean }>`
  display: inline-block;
  margin-right: 0.55em;
  position: relative;
  white-space: nowrap;

  ${p =>
    p.$hasError &&
    `
    &::after {
      background: var(--color-incorrect);
      border-radius: 1px;
      bottom: 2px;
      content: '';
      height: 2px;
      left: 0;
      opacity: 0.4;
      position: absolute;
      right: 0;
    }
  `}
`;

const CharSpan = styled.span<{ $status: CharStatus }>`
  color: ${p =>
    p.$status === 'correct'
      ? 'var(--color-correct)'
      : p.$status === 'incorrect'
        ? 'var(--color-incorrect)'
        : 'var(--color-pending)'};
  text-decoration: ${p => (p.$status === 'incorrect' ? 'underline wavy var(--color-incorrect)' : 'none')};
`;

const CursorBar = styled.span`
  animation: blink 1.1s step-start infinite;
  background: var(--color-cursor);
  border-radius: 1px;
  display: inline-block;
  height: 1.15em;
  margin-right: -2px;
  vertical-align: text-bottom;
  width: 2px;
`;

const HiddenInput = styled.input`
  height: 1px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  width: 1px;
`;

const ActionRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  gap: var(--space-sm);
  margin-top: var(--space-lg);
`;

const ActionBtn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  align-items: center;
  background: ${p => (p.$variant === 'primary' ? 'var(--color-accent-glow)' : 'transparent')};
  border: 1px solid ${p => (p.$variant === 'primary' ? 'var(--color-accent)' : 'var(--color-border-strong)')};
  border-radius: var(--radius-md);
  color: ${p => (p.$variant === 'primary' ? 'var(--color-accent)' : 'var(--color-text-secondary)')};
  cursor: pointer;
  display: flex;
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  gap: 6px;
  letter-spacing: var(--letter-spacing-label);
  padding: 8px 20px;
  text-transform: uppercase;
  transition: all var(--transition-base);

  &:hover {
    background: var(--color-bg-subtle);
    border-color: var(--color-accent);
    color: var(--color-text-primary);
  }

  kbd {
    background: var(--color-bg-subtle);
    border: 1px solid var(--color-border-strong);
    border-radius: var(--radius-sm);
    font-family: var(--font-display);
    font-size: 0.65rem;
    padding: 1px 5px;
  }
`;

/* ─── RESULTS CARD ──────────────────────────────────────────────────────── */
const ResultsCard = styled.div`
  animation: fadeSlideUp var(--transition-slow) ease both;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-surface);
  overflow: hidden;
`;

const ResultsTop = styled.div<{ $ratingColor: string }>`
  background: var(--color-bg-surface);
  border-bottom: 1px solid var(--color-border);
  padding: var(--space-lg) var(--space-xl);
  position: relative;

  &::before {
    background: ${p => p.$ratingColor};
    content: '';
    height: 2px;
    left: 0;
    opacity: 0.6;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

const RatingLabel = styled.div<{ $color: string }>`
  color: ${p => p.$color};
  font-family: var(--font-ui);
  font-size: 2rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.03em;
  line-height: 1;
  margin-bottom: 4px;
`;

const RatingDesc = styled.div`
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  letter-spacing: var(--letter-spacing-label);
`;

const ResultsMainStats = styled.div`
  border-bottom: 1px solid var(--color-border);
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  padding: var(--space-lg) var(--space-xl);
`;

const MainStat = styled.div`
  border-right: 1px solid var(--color-border);
  flex: 1;
  min-width: 120px;
  padding: 0 var(--space-lg) 0 0;
  margin-right: var(--space-lg);

  &:last-of-type {
    border-right: none;
    padding-right: 0;
    margin-right: 0;
  }
`;

const BigNum = styled.div<{ $color: string }>`
  animation: fadeSlideUp var(--transition-slow) ease both;
  color: ${p => p.$color};
  font-family: var(--font-display);
  font-size: var(--font-size-stat);
  font-weight: var(--font-weight-bold);
  line-height: 1;
`;

const SubNum = styled.div`
  color: var(--color-text-muted);
  font-family: var(--font-display);
  font-size: 0.8rem;
  margin-top: 2px;
`;

const ResultsChars = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-md);
  padding: var(--space-md) var(--space-xl);
`;

const CharChip = styled.span<{ $color: string }>`
  color: ${p => p.$color};
  font-family: var(--font-display);
  font-size: 0.875rem;
`;

const CharLabel = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  letter-spacing: var(--letter-spacing-label);
  margin-left: var(--space-lg);
  text-transform: uppercase;
`;

const ResultsChart = styled.div`
  border-bottom: 1px solid var(--color-border);
  padding: var(--space-md) var(--space-xl);
`;

const ChartTitle = styled.div`
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  letter-spacing: var(--letter-spacing-label);
  margin-bottom: var(--space-sm);
  text-transform: uppercase;
`;

const ResultsActions = styled.div`
  align-items: center;
  display: flex;
  gap: var(--space-sm);
  justify-content: flex-end;
  padding: var(--space-md) var(--space-xl);
`;

/* ─── WPM CHART ─────────────────────────────────────────────────────────── */
function WpmChart({ history }: { history: WpmPoint[] }) {
  if (history.length < 2) return null;
  const W = 800;
  const H = 70;
  const maxWpm = Math.max(...history.map(p => p.wpm), 1);
  const wpmPts = history.map((p, i) => `${(i / (history.length - 1)) * W},${H - (p.wpm / maxWpm) * (H - 8) - 4}`);
  const rawPts = history.map((p, i) => `${(i / (history.length - 1)) * W},${H - (p.rawWpm / maxWpm) * (H - 8) - 4}`);
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 70, display: 'block' }}>
        <polyline points={rawPts.join(' ')} fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeDasharray="4 3" />
        <polyline points={wpmPts.join(' ')} fill="none" stroke="var(--color-wpm)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
        <CharChip $color="var(--color-wpm)">&#9644; wpm</CharChip>
        <CharChip $color="var(--color-text-muted)">&#9644;&#9644; raw</CharChip>
      </div>
    </>
  );
}

/* ─── RESULTS VIEW ──────────────────────────────────────────────────────── */
interface ResultsViewProps {
  stats: Stats;
  wpmHistory: WpmPoint[];
  timerMode: TimerMode;
  difficulty: Difficulty;
  onRestart: () => void;
  onShare: () => void;
  onHistory: () => void;
}

function ResultsView({ stats, wpmHistory, timerMode, difficulty, onRestart, onShare, onHistory }: ResultsViewProps) {
  const rating = getRating(stats.wpm);
  const wpm = useCountUp(stats.wpm);
  const rawWpm = useCountUp(stats.rawWpm);
  const accuracy = useCountUp(stats.accuracy);

  return (
    <ResultsCard>
      {/* Rating headline */}
      <ResultsTop $ratingColor={rating.color}>
        <RatingLabel $color={rating.color}>{rating.label}</RatingLabel>
        <RatingDesc>{rating.description}</RatingDesc>
      </ResultsTop>

      {/* Main stats row */}
      <ResultsMainStats>
        <MainStat>
          <BigNum $color="var(--color-wpm)">{wpm}</BigNum>
          <StatLabel>wpm</StatLabel>
          <SubNum>raw {rawWpm}</SubNum>
        </MainStat>
        <MainStat>
          <BigNum $color="var(--color-accuracy)">{accuracy}%</BigNum>
          <StatLabel>accuracy</StatLabel>
          {stats.accuracy === 100 && <SubNum style={{ color: 'var(--color-correct)' }}>perfect</SubNum>}
        </MainStat>
        <MainStat>
          <BigNum $color="var(--color-time)">{timerMode}s</BigNum>
          <StatLabel>time</StatLabel>
        </MainStat>
        <MainStat>
          <BigNum $color={
            difficulty === 'easy' ? 'var(--color-correct)'
            : difficulty === 'hard' ? 'var(--color-incorrect)'
            : 'var(--color-wpm)'
          } style={{ fontSize: '1.75rem', textTransform: 'uppercase' }}>{difficulty}</BigNum>
          <StatLabel>difficulty</StatLabel>
        </MainStat>
      </ResultsMainStats>

      {/* Characters */}
      <ResultsChars>
        <CharLabel>Characters</CharLabel>
        <CharChip $color="var(--color-correct)">{stats.correct} correct</CharChip>
        <CharChip $color="var(--color-incorrect)">{stats.incorrect} incorrect</CharChip>
        {stats.extra > 0 && <CharChip $color="var(--color-text-muted)">{stats.extra} extra</CharChip>}
        {stats.missed > 0 && <CharChip $color="var(--color-text-muted)">{stats.missed} missed</CharChip>}
      </ResultsChars>

      {/* WPM chart */}
      {wpmHistory.length >= 2 && (
        <ResultsChart>
          <ChartTitle>WPM over time</ChartTitle>
          <WpmChart history={wpmHistory} />
        </ResultsChart>
      )}

      {/* Actions */}
      <ResultsActions>
        <ActionBtn onClick={onHistory}>History</ActionBtn>
        <ActionBtn onClick={onShare} $variant="primary">Share</ActionBtn>
        <ActionBtn onClick={onRestart}>
          Restart <kbd>tab</kbd>
        </ActionBtn>
      </ResultsActions>
    </ResultsCard>
  );
}

/* ─── MAIN COMPONENT ────────────────────────────────────────────────────── */
export default function TypingTest() {
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [timerMode, setTimerMode] = useState<TimerMode>(30);
  const [words, setWords] = useState<WordData[]>(() => generateWords(WORD_POOL, 'medium'));
  const [activeWordIdx, setActiveWordIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState<number>(30);
  const [elapsed, setElapsed] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [wpmHistory, setWpmHistory] = useState<WpmPoint[]>([]);
  const [finalStats, setFinalStats] = useState<Stats | null>(null);
  const [showShare, setShowShare] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const wordsRef = useRef<HTMLDivElement>(null);
  const wordRefs = useRef<(HTMLSpanElement | null)[]>([]);

  /* timer */
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => {
      setTimeLeft(t => (t <= 1 ? 0 : t - 1));
      setElapsed(e => e + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  /* WPM history snapshot */
  useEffect(() => {
    if (!isRunning || elapsed === 0) return;
    const s = computeStats(words, elapsed);
    setWpmHistory(prev => [...prev, { second: elapsed, wpm: s.wpm, rawWpm: s.rawWpm }]);
  }, [elapsed]);

  /* finish */
  useEffect(() => {
    if (timeLeft === 0 && isRunning) {
      setIsRunning(false);
      setIsFinished(true);
      const s = computeStats(words, timerMode);
      setFinalStats(s);
      saveHistoryEntry({
        date: Date.now(),
        wpm: s.wpm,
        rawWpm: s.rawWpm,
        accuracy: s.accuracy,
        timeMode: timerMode,
        difficulty,
        correct: s.correct,
        incorrect: s.incorrect,
        extra: s.extra,
      });
      setHistoryKey(k => k + 1);
    }
  }, [timeLeft, isRunning]);

  /* scroll active word into view */
  useEffect(() => {
    const container = wordsRef.current;
    const activeEl = wordRefs.current[activeWordIdx];
    if (!container || !activeEl) return;
    const containerRect = container.getBoundingClientRect();
    const wordRect = activeEl.getBoundingClientRect();
    const lineHeight = parseFloat(getComputedStyle(container).lineHeight) || 40;
    if ((wordRect.top - containerRect.top) / lineHeight >= LINE_ROWS - 1) {
      container.scrollTop += lineHeight;
    }
  }, [activeWordIdx]);

  const reset = useCallback(
    (newDifficulty = difficulty, newMode = timerMode) => {
      setWords(generateWords(WORD_POOL, newDifficulty));
      setActiveWordIdx(0);
      setTimeLeft(newMode);
      setElapsed(0);
      setIsRunning(false);
      setIsFinished(false);
      setWpmHistory([]);
      setFinalStats(null);
      setShowShare(false);
      wordRefs.current = [];
      if (wordsRef.current) wordsRef.current.scrollTop = 0;
      setTimeout(() => inputRef.current?.focus(), 50);
    },
    [difficulty, timerMode],
  );

  const changeMode = (mode: TimerMode) => {
    setTimerMode(mode);
    reset(difficulty, mode);
  };

  const changeDifficulty = (diff: Difficulty) => {
    setDifficulty(diff);
    reset(diff, timerMode);
  };

  /* keyboard */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      reset();
      return;
    }
    if (isFinished) return;
    if (!isRunning) {
      if (e.key.length === 1 || e.key === 'Backspace') setIsRunning(true);
      else return;
    }
    if (e.key === 'Backspace') {
      e.preventDefault();
      const current = words[activeWordIdx];
      if (current.typed.length > 0) {
        setWords(prev => {
          const next = [...prev];
          next[activeWordIdx] = { ...next[activeWordIdx], typed: e.ctrlKey ? '' : next[activeWordIdx].typed.slice(0, -1) };
          return next;
        });
      } else if (activeWordIdx > 0) {
        setWords(prev => {
          const next = [...prev];
          next[activeWordIdx - 1] = { ...next[activeWordIdx - 1], completed: false };
          return next;
        });
        setActiveWordIdx(p => p - 1);
      }
      return;
    }
    if (e.key === ' ') {
      e.preventDefault();
      if (words[activeWordIdx].typed.length === 0) return;
      setWords(prev => {
        const next = [...prev];
        next[activeWordIdx] = { ...next[activeWordIdx], completed: true };
        return next;
      });
      setActiveWordIdx(p => p + 1);
      return;
    }
    if (e.key.length === 1) {
      const current = words[activeWordIdx];
      if (current.typed.length >= current.target.length + MAX_EXTRA) return;
      setWords(prev => {
        const next = [...prev];
        next[activeWordIdx] = { ...next[activeWordIdx], typed: next[activeWordIdx].typed + e.key };
        return next;
      });
    }
  };

  const liveStats = isRunning ? computeStats(words, Math.max(elapsed, 1)) : null;
  const visibleHeight = `calc(var(--line-height-typing) * var(--font-size-typing) * ${LINE_ROWS})`;

  const renderWord = (word: WordData, wordIdx: number) => {
    const isActive = wordIdx === activeWordIdx;
    const cursorPos = word.typed.length;
    const maxLen = Math.max(word.target.length, word.typed.length);
    const hasError = word.completed && word.typed !== word.target;
    const chars: React.ReactNode[] = [];

    for (let i = 0; i < maxLen; i++) {
      if (isActive && i === cursorPos) chars.push(<CursorBar key="cur" />);
      if (i < word.typed.length) {
        const status: CharStatus =
          i < word.target.length && word.typed[i] === word.target[i] ? 'correct' : 'incorrect';
        chars.push(<CharSpan key={`t${i}`} $status={status}>{word.typed[i]}</CharSpan>);
      } else {
        chars.push(<CharSpan key={`p${i}`} $status="pending">{word.target[i]}</CharSpan>);
      }
    }
    if (isActive && cursorPos >= maxLen) chars.push(<CursorBar key="cur-end" />);

    return (
      <WordSpan key={wordIdx} $hasError={hasError} ref={el => { wordRefs.current[wordIdx] = el; }}>
        {chars}
      </WordSpan>
    );
  };

  const shareStats: ShareStats | null = finalStats
    ? {
        ...finalStats,
        timeMode: timerMode,
        difficulty,
        rating: getRating(finalStats.wpm).label,
        ratingColor: getRating(finalStats.wpm).color,
      }
    : null;

  return (
    <Wrapper>
      {/* Mode bar */}
      <ModeBar>
        {DIFFICULTIES.map(d => (
          <DiffBtn key={d} $diff={d} $active={difficulty === d} onClick={() => changeDifficulty(d)}>
            {d}
          </DiffBtn>
        ))}
        <Divider>|</Divider>
        {TIMER_MODES.map(m => (
          <ModeBtn key={m} $active={timerMode === m} onClick={() => changeMode(m)}>
            {m}s
          </ModeBtn>
        ))}
        <Divider>|</Divider>
        <HistoryBtn onClick={() => setShowHistory(s => !s)} $active={showHistory}>
          History
        </HistoryBtn>
      </ModeBar>

      {isFinished && finalStats ? (
        <ResultsView
          stats={finalStats}
          wpmHistory={wpmHistory}
          timerMode={timerMode}
          difficulty={difficulty}
          onRestart={reset}
          onShare={() => setShowShare(true)}
          onHistory={() => setShowHistory(s => !s)}
        />
      ) : (
        <>
          {/* Live stats */}
          <LiveBar>
            <StatGroup>
              <StatValue $color="var(--color-time)">{timeLeft}</StatValue>
              <StatLabel>seconds</StatLabel>
            </StatGroup>
            {liveStats && (
              <>
                <StatGroup>
                  <StatValue $color="var(--color-wpm)">{liveStats.wpm}</StatValue>
                  <StatLabel>wpm</StatLabel>
                </StatGroup>
                <StatGroup>
                  <StatValue $color="var(--color-accuracy)">{liveStats.accuracy}%</StatValue>
                  <StatLabel>accuracy</StatLabel>
                </StatGroup>
                <StatGroup>
                  <StatValue $color="var(--color-text-secondary)" $sm>{liveStats.rawWpm}</StatValue>
                  <StatLabel>raw wpm</StatLabel>
                </StatGroup>
              </>
            )}
          </LiveBar>

          {/* Typing area */}
          <TypingBox $focused={isFocused} onClick={() => inputRef.current?.focus()} tabIndex={-1}>
            <HiddenInput
              ref={inputRef}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              readOnly
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            <WordsScroll ref={wordsRef} style={{ maxHeight: visibleHeight }}>
              {words.map((w, i) => renderWord(w, i))}
            </WordsScroll>
            <FocusHint $visible={!isFocused}>click to focus</FocusHint>
          </TypingBox>

          <ActionRow>
            <ActionBtn onClick={() => reset()}>Restart <kbd>tab</kbd></ActionBtn>
          </ActionRow>
        </>
      )}

      {/* History panel */}
      {showHistory && (
        <HistoryPanel onClose={() => setShowHistory(false)} refreshKey={historyKey} />
      )}

      {/* Share modal */}
      {showShare && shareStats && (
        <ShareModal stats={shareStats} onClose={() => setShowShare(false)} />
      )}
    </Wrapper>
  );
}
